import React, { useMemo, useState } from 'react';
import AISummarySetting from '../../component/AISummarySetting';
import { useChannelShow, useSaveCustomSummaryReport } from '../../hooks';
import { PromptConfirmItemType } from '@op/components/GPT/types';
import { Button, Form, Modal } from 'tea-component';
import GPT from '@op/components/GPT';
import useSaveForm from '@op/components/GPT/hooks/useSaveForm';
import { GPTBody } from '@op/pages/AIAnalysisHub/components';
import useReportCreateSuccess from '@op/pages/AIAnalysisHub/hooks/useReportCreateSuccess';
import useLanguage from '@op/hooks/useLanguage';

export default function GenerateSummary(props: any) {
  const { gameInfo, userInfo, wholeGameInfo, changeTab } = props;
  const { commentList, socialList, backCondOriginData } = useChannelShow();
  const [promptInfo, setPromptInfo] = useState<PromptConfirmItemType>({});
  const [filter, setFilter] = useState(null);

  // GPT 生成完成后自动滚动到内容区域（每次新 taskId 才触发）
  const { reportRef: contentRef } = useReportCreateSuccess({
    trigger: !!promptInfo.taskId,
    scrollKey: promptInfo.taskId,
    delay: 800,
    maxRetries: 10,
    retryInterval: 300,
    scrollOptions: { behavior: 'smooth', block: 'end' },
  });

  const {
    saveVisible,
    saveUserSummary,
    saveTipsElement,
    saveTipsIcon,
    setIsSave,
    setOriFilter,
    setSaveParams,
    saveParams,
    toggleSaveWindow,
    cardSaveIcon,
  } = useSaveCustomSummaryReport({
    gameInfo,
    changeTab,
  });

  return (
    <>
      <AISummarySetting
        handleConfirm={(
          promptInfo: PromptConfirmItemType,
          filter: any,
          options: {
            isSave: boolean;
            saveParams: any;
            createShortIdParams: any;
          },
        ) => {
          setPromptInfo(promptInfo);
          setFilter(filter);

          if (options) {
            setIsSave(!!options?.isSave);
            setSaveParams(options?.saveParams || null);
            setOriFilter(options?.createShortIdParams || null);
          }
        }}
        backCondOriginData={backCondOriginData}
        gameInfo={gameInfo}
        userInfo={userInfo}
        wholeGameInfo={wholeGameInfo}
        changeTab={changeTab}
      >
        <div ref={contentRef} className="dbo-generate-summary-body">
          <GPTBody
            gameInfo={gameInfo}
            contentRef={contentRef}
            filter={filter}
            saveClick={toggleSaveWindow}
            loadingElement={saveTipsElement}
            promptInfo={promptInfo}
            commentList={commentList}
            socialList={socialList}
            cardSaveIcon={cardSaveIcon}
            stopClick={() => {
              setPromptInfo({});
              setFilter(null);
              setIsSave(false);
              setSaveParams(null);
              setOriFilter(null);
            }}
            saveTipsIcon={saveTipsIcon}
            wholeGameInfo={wholeGameInfo}
            showStatsHeader
          />
        </div>
      </AISummarySetting>

      {saveVisible && (
        <SaveAnalysisModal
          visible={saveVisible}
          userInfo={userInfo}
          saveUserSummary={saveUserSummary}
          getSaveWindowsName={() => saveParams?.name || ''}
          close={toggleSaveWindow}
        ></SaveAnalysisModal>
      )}
    </>
  );
}

function SaveAnalysisModal({
  visible,
  close,
  saveUserSummary,
  getSaveWindowsName,
  userInfo,
}: {
  visible: boolean;
  close?: () => void;
  saveUserSummary: (name: string, otherParams: any) => Promise<boolean>;
  getSaveWindowsName: () => string;
  userInfo: any;
}) {
  const { formatMessage } = useLanguage();

  const {
    emailList,
    setShowEmailMessage,
    aiSaveTitle,
    setAiSaveTitle,
    showEmailMessage,
    setEmailList,
    saveEmailList,
  } = useSaveForm({
    userInfo,
    getDefaultSaveName: getSaveWindowsName,
    defaultIsSave: true,
  });

  async function submit() {
    saveEmailList();
    const params: any = {};
    if (emailList.length > 0) {
      params.email = emailList.join(',');
    }
    const result = await saveUserSummary(aiSaveTitle, params);

    if (result) close?.();
  }

  const isError = useMemo(
    () => aiSaveTitle.trim() === '' || showEmailMessage,
    [aiSaveTitle, showEmailMessage],
  );

  return (
    <Modal
      visible={visible}
      caption={formatMessage({ id: 'Save to Analysis List' })}
      onClose={close}
      className="dbo-save-analysis-modal"
    >
      <Modal.Body>
        <p>
          {formatMessage({
            id: 'The analysis will take 15 to 30 minutes to generate.',
          })}
        </p>
        <p>
          {formatMessage(
            {
              id: 'You can view the analysis in the “{text}”.',
            },
            {
              text: <b>{formatMessage({ id: 'Saved Analysis List' })}</b>,
            },
          )}
        </p>
        <Form>
          <GPT.GPTSummaryNameForm
            name={aiSaveTitle}
            setName={setAiSaveTitle}
            setEmailList={setEmailList}
            emailList={emailList}
            showEmailMessage={showEmailMessage}
            setShowEmailMessage={setShowEmailMessage}
            needSummaryName
          ></GPT.GPTSummaryNameForm>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button type="primary" disabled={isError} onClick={submit}>
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={close}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
